import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { KeyRound, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { ConfirmDelete } from "@/components/common/ConfirmDelete";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, formatApiError } from "@/lib/api";

const SOURCE = { admin: "Défini dans l'admin", env: "Défini dans l'environnement", none: "Non défini" };

const SecretRow = ({ s }) => {
  const qc = useQueryClient();
  const [v, setV] = useState("");
  const [busy, setBusy] = useState(false);
  const refresh = () => ["admin-secrets", "platform-status", "admin-overview"].forEach((k) => qc.invalidateQueries({ queryKey: [k] }));
  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { await api.put(`/admin/secrets/${s.name}`, { value: v.trim() }); setV(""); toast.success(`${s.label} enregistré`); refresh(); }
    catch (err) { toast.error(formatApiError(err)); } finally { setBusy(false); }
  };
  const clear = async () => {
    try { await api.delete(`/admin/secrets/${s.name}`); toast.success("Valeur de l'admin supprimée"); refresh(); } catch (err) { toast.error(formatApiError(err)); }
  };
  return (
    <li className="space-y-2 py-4" data-testid={`secret-${s.name}`}>
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <b className="flex-1">{s.label}</b>
        <span className="num text-xs text-muted-foreground" data-testid={`secret-masked-${s.name}`}>{s.masked || "—"}</span>
        <span className={`rounded-full px-2.5 py-0.5 text-[11px] font-semibold ${s.source === "none" ? "bg-secondary text-muted-foreground" : "bg-success/15 text-success"}`} data-testid={`secret-source-${s.name}`}>{SOURCE[s.source]}</span>
      </div>
      <form onSubmit={save} className="flex flex-wrap gap-2">
        <Input type="password" autoComplete="off" value={v} onChange={(e) => setV(e.target.value)} placeholder="Coller la nouvelle valeur" minLength={8} required className="min-w-0 flex-1 basis-56" data-testid={`secret-input-${s.name}`} />
        <Button type="submit" disabled={busy} className="rounded-full" data-testid={`secret-save-${s.name}`}>{busy && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}Enregistrer</Button>
        {s.source === "admin" && <ConfirmDelete title="Supprimer cette clé ?" text={`${s.label} : la valeur de l'environnement (s'il y en a une) sera utilisée à la place.`} onConfirm={clear}>
          <Button type="button" variant="outline" className="rounded-full" data-testid={`secret-clear-${s.name}`}>Supprimer</Button></ConfirmDelete>}
      </form>
    </li>
  );
};

const BridgeTest = () => {
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);
  const run = async () => { setBusy(true); try { setRes((await api.post("/admin/bridge/test")).data); } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); } };
  return (
    <div className="mt-4 space-y-2 rounded-xl bg-muted/50 p-3" data-testid="bridge-test">
      <Button variant="outline" size="sm" className="rounded-full" onClick={run} disabled={busy} data-testid="bridge-test-run">{busy && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}Tester la connexion Bridge</Button>
      {res && <div className="space-y-1 text-xs" data-testid="bridge-test-result">
        <p>Identifiant client : <b>{res.client_id_type}</b> · version API {res.version}</p>
        {res.steps.map((s) => <p key={s.step} className={s.ok ? "text-success" : "text-destructive"}><b>{s.ok ? "OK" : "Échec"}</b> — {s.step} ({s.status ?? "pas de réponse"}){s.error && ` : ${typeof s.error === "string" ? s.error : JSON.stringify(s.error)}`}</p>)}
      </div>}
    </div>
  );
};

export const SecretsPanel = () => {
  const { data } = useQuery({ queryKey: ["admin-secrets"], queryFn: () => api.get("/admin/secrets").then((r) => r.data) });
  if (!data) return null;
  return (
    <section className="card-surface p-6" data-testid="secrets-panel">
      <h2 className="flex items-center gap-2 text-lg font-bold"><KeyRound className="h-5 w-5" />Clés Bridge</h2>
      <p className="mt-1 text-xs text-muted-foreground">Les valeurs sont chiffrées et ne sont jamais réaffichées (seuls les 4 derniers caractères). Les clés définies dans les Secrets de publication sont prioritaires ; une valeur saisie ici sert uniquement si aucune clé n'y est définie.</p>
      <ul className="divide-y divide-border">{data.map((s) => <SecretRow key={s.name} s={s} />)}</ul>
      <BridgeTest />
    </section>
  );
};
